/**
 * Keeps a list screen where the user left it.
 *
 * The complaint this answers: pick an employee on page 17, edit them, come back
 * — and the list restarts at page 1 of 28, so you click Next sixteen times.
 * The page number, filters and sorting lived in component state, and leaving
 * the screen threw them away.
 *
 * They now live in the URL (`/employees?page=17&status=active`), which means
 * the browser Back button, a refresh and a shared link all land in the same
 * place. `listReturnPath` lets a "Back to list" button rebuild that URL, and
 * the anchor helpers scroll the row you clicked back under your eyes.
 *
 * Entering a list with no query — clicking it in the sidebar — is a clean
 * start, page 1, by design.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const returnKey = (listKey) => `list-return:${listKey}`;
const anchorKey = (listKey) => `list-anchor:${listKey}`;

const read = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    /* private browsing — the feature degrades, nothing breaks */
  }
};

/** The list URL as the user last had it, for a "Back to list" button. */
export function listReturnPath(listKey, basePath) {
  const query = read(returnKey(listKey));
  return query ? `${basePath}?${query}` : basePath;
}

/** Remember which row was opened, so the list can scroll back to it. */
export function rememberListAnchor(listKey, rowId) {
  if (rowId) write(anchorKey(listKey), String(rowId));
}

/**
 * URL-backed view state for a list screen.
 *
 * @param listKey  stable name for this list, e.g. 'employees'
 * @param defaults every key the list keeps in the URL, with its default value.
 *                 A value equal to its default is left out of the URL, so a
 *                 clean list has a clean address.
 * @returns [values, setValues] — setValues takes a patch, not a whole object.
 */
export function useListViewState(listKey, defaults) {
  const location = useLocation();
  const navigate = useNavigate();
  const defaultsRef = useRef(defaults);
  // Buffers the params between a change and the URL actually landing, so two
  // clicks in quick succession don't each start from the same stale address.
  const pendingRef = useRef(null);

  const values = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const out = {};
    Object.entries(defaultsRef.current).forEach(([key, fallback]) => {
      const raw = params.get(key);
      if (raw === null || raw === '') {
        out[key] = fallback;
        return;
      }
      out[key] = typeof fallback === 'number' ? (Number.parseInt(raw, 10) || fallback) : raw;
    });
    return out;
  }, [location.search]);

  useEffect(() => {
    pendingRef.current = null;
  }, [location.search]);

  const setValues = useCallback((patch) => {
    const params = pendingRef.current || new URLSearchParams(window.location.search);
    Object.entries(patch).forEach(([key, value]) => {
      const fallback = defaultsRef.current[key];
      const isDefault = value === undefined || value === null || value === '' || String(value) === String(fallback);
      if (isDefault) params.delete(key);
      else params.set(key, String(value));
    });
    pendingRef.current = params;
    const search = params.toString();
    // replace, not push: paging and filtering should not stack up history
    // entries the user has to click Back through.
    navigate({ pathname: window.location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [navigate]);

  // Where to come back to.
  useEffect(() => {
    write(returnKey(listKey), location.search.replace(/^\?/, ''));
  }, [listKey, location.search]);

  // A clean entry (sidebar click) starts fresh, so drop any stale row anchor.
  const enteredClean = useRef(!location.search);
  useEffect(() => {
    if (enteredClean.current) write(anchorKey(listKey), null);
  }, [listKey]);

  return [values, setValues];
}

/**
 * A text box whose value belongs in the URL.
 *
 * Typing stays local and instant; the URL catches up once typing pauses.
 * Pushing every keystroke through the address bar loses characters — typing
 * "MOHAMMED" arrived as "MD" — because the input reads back a URL that is one
 * render behind the keyboard.
 *
 * @param urlValue the value currently in the URL
 * @param commit   stable callback that writes a new value to the URL
 */
export function useDebouncedUrlText(urlValue, commit, delay = 400) {
  const [text, setText] = useState(urlValue);
  const pushed = useRef(urlValue);

  // Follow the URL when it changes from elsewhere: Clear filters, the browser
  // Back button, or coming back to a remembered list.
  useEffect(() => {
    if (urlValue !== pushed.current) {
      pushed.current = urlValue;
      setText(urlValue);
    }
  }, [urlValue]);

  useEffect(() => {
    if (text === urlValue) return undefined;
    const timer = setTimeout(() => {
      pushed.current = text;
      commit(text);
    }, delay);
    return () => clearTimeout(timer);
  }, [text, urlValue, delay, commit]);

  return [text, setText];
}

/**
 * Scrolls back to the row the user opened and marks it briefly.
 *
 * Returns the row id to highlight — as state, not a DOM class, because React
 * rewrites `className` on the next render and would silently wipe it.
 *
 * @param ready true when the rows are on screen (data loaded), not before.
 */
export function useListAnchor(listKey, ready) {
  const [anchoredId, setAnchoredId] = useState(null);
  const scrolled = useRef(false);

  useEffect(() => {
    if (!ready || scrolled.current) return undefined;
    const rowId = read(anchorKey(listKey));
    if (!rowId) return undefined;

    let fade;
    const frame = requestAnimationFrame(() => {
      const row = document.querySelector(`[data-row-anchor="${rowId}"]`);
      if (!row) return;
      scrolled.current = true;
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setAnchoredId(rowId);
      // The anchor is dropped on a timer rather than the moment it is used: a
      // re-mount right after the return (a context settling, a second fetch)
      // would otherwise land with nothing to highlight.
      fade = setTimeout(() => {
        write(anchorKey(listKey), null);
        setAnchoredId(null);
      }, 2400);
    });
    return () => {
      cancelAnimationFrame(frame);
      if (fade) clearTimeout(fade);
    };
  }, [listKey, ready]);

  return anchoredId;
}
